import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  Legend,
  ComposedChart,
  ScatterChart,
  Scatter
} from 'recharts';
import { 
  BookOpen, 
  CheckCircle, 
  Clock, 
  Target, 
  Award,
  Heart,
  BarChart3,
  BookX,
  ShoppingCart,
  TrendingUp,
  Calendar,
  DollarSign,
  Star,
  BookMarked,
  Users,
  Zap,
  Globe,
  Activity,
  PieChart as PieChartIcon,
  Target as TargetIcon
} from 'lucide-react';
import { useAppState } from '../context/AppStateContext';

const GlobalStatistics: React.FC = () => {
  const { state } = useAppState();

  const statistics = useMemo(() => {
    const libros = state.libros.filter(book => book.estado !== 'wishlist');
    const coleccionesManga = state.coleccionesManga;
    
    // Estadísticas básicas combinadas
    const totalLibros = libros.length;
    const totalManga = coleccionesManga.length;
    const totalColeccion = totalLibros + totalManga;
    
    // Estados de lectura combinados
    const librosLeidos = libros.filter(book => book.estado === 'leido').length;
    const tomosMangaLeidos = coleccionesManga.reduce((sum, c) => sum + c.tomosLeidos, 0);
    const totalLeidos = librosLeidos + tomosMangaLeidos;
    
    const librosLeyendo = libros.filter(book => book.estado === 'leyendo').length;
    const tomosMangaLeyendo = coleccionesManga.reduce((sum, c) => sum + c.tomos.filter(t => t.estado === 'leyendo').length, 0);
    const totalLeyendo = librosLeyendo + tomosMangaLeyendo;
    
    const librosTBR = libros.filter(book => book.estado === 'tbr').length;
    const tomosMangaTBR = coleccionesManga.reduce((sum, c) => sum + c.tomos.filter(t => t.estado === 'tbr').length, 0);
    const totalTBR = librosTBR + tomosMangaTBR;
    
    // Valor total combinado
    const valorLibros = libros
      .filter(book => book.precio && book.precio > 0)
      .reduce((sum, book) => sum + (book.precio || 0), 0);
    
    const valorManga = coleccionesManga.reduce((sum, c) => {
      if (c.precioPorTomo && c.precioPorTomo > 0) {
        const tomosReales = c.tomos.filter(t => 
          t.fechaCompra || 
          t.estado === 'comprado' || 
          t.estado === 'leido' || 
          t.estado === 'leyendo'
        ).length;
        return sum + (tomosReales * c.precioPorTomo);
      }
      return sum;
    }, 0);
    
    const valorTotal = valorLibros + valorManga;
    
    // Páginas totales
    const paginasLibros = libros
      .filter(book => book.estado === 'leido')
      .reduce((sum, book) => sum + (book.paginas || 0), 0);
    
    const paginasManga = coleccionesManga.reduce((sum, c) => {
      return sum + c.tomos
        .filter(t => t.estado === 'leido')
        .reduce((sumTomo, tomo) => sumTomo + (c.precioPorTomo ? 200 : 0), 0); // Estimación de páginas por tomo
    }, 0);
    
    const paginasTotales = paginasLibros + paginasManga;
    
    // Estadísticas por género combinadas
    const generosCombinados: Record<string, { libros: number; manga: number; leidos: number; valor: number }> = {};
    
    // Agregar géneros de libros
    libros.forEach(book => {
      if (book.genero) {
        if (!generosCombinados[book.genero]) {
          generosCombinados[book.genero] = { libros: 0, manga: 0, leidos: 0, valor: 0 };
        }
        generosCombinados[book.genero].libros++;
        if (book.estado === 'leido') generosCombinados[book.genero].leidos++;
        if (book.precio) generosCombinados[book.genero].valor += book.precio;
      }
    });
    
    // Agregar géneros de manga
    coleccionesManga.forEach(coleccion => {
      if (coleccion.genero) {
        if (!generosCombinados[coleccion.genero]) {
          generosCombinados[coleccion.genero] = { libros: 0, manga: 0, leidos: 0, valor: 0 };
        }
        generosCombinados[coleccion.genero].manga++;
        generosCombinados[coleccion.genero].leidos += coleccion.tomosLeidos;
        if (coleccion.precioPorTomo) {
          generosCombinados[coleccion.genero].valor += coleccion.tomos.filter(t => 
            t.fechaCompra || 
            t.estado === 'comprado' || 
            t.estado === 'leido' || 
            t.estado === 'leyendo'
          ).length * coleccion.precioPorTomo;
        }
      }
    });
    
    // Estadísticas por editorial
    const editorialesCombinadas: Record<string, { libros: number; manga: number; leidos: number; valor: number }> = {};
    
    libros.forEach(book => {
      if (book.editorial) {
        if (!editorialesCombinadas[book.editorial]) {
          editorialesCombinadas[book.editorial] = { libros: 0, manga: 0, leidos: 0, valor: 0 };
        }
        editorialesCombinadas[book.editorial].libros++;
        if (book.estado === 'leido') editorialesCombinadas[book.editorial].leidos++;
        if (book.precio) editorialesCombinadas[book.editorial].valor += book.precio;
      }
    });
    
    coleccionesManga.forEach(coleccion => {
      if (coleccion.editorial) {
        if (!editorialesCombinadas[coleccion.editorial]) {
          editorialesCombinadas[coleccion.editorial] = { libros: 0, manga: 0, leidos: 0, valor: 0 };
        }
        editorialesCombinadas[coleccion.editorial].manga++;
        editorialesCombinadas[coleccion.editorial].leidos += coleccion.tomosLeidos;
        if (coleccion.precioPorTomo) {
          editorialesCombinadas[coleccion.editorial].valor += coleccion.tomos.filter(t => 
            t.fechaCompra || 
            t.estado === 'comprado' || 
            t.estado === 'leido' || 
            t.estado === 'leyendo'
          ).length * coleccion.precioPorTomo;
        }
      }
    });
    
    // Estadísticas por idioma
    const idiomasCombinados: Record<string, { libros: number; manga: number; leidos: number; valor: number }> = {};
    
    libros.forEach(book => {
      const idioma = book.idioma || 'Desconocido';
      if (!idiomasCombinados[idioma]) {
        idiomasCombinados[idioma] = { libros: 0, manga: 0, leidos: 0, valor: 0 };
      }
      idiomasCombinados[idioma].libros++;
      if (book.estado === 'leido') idiomasCombinados[idioma].leidos++;
      if (book.precio) idiomasCombinados[idioma].valor += book.precio;
    });
    
    coleccionesManga.forEach(coleccion => {
      const idioma = coleccion.idioma || 'Desconocido';
      if (!idiomasCombinados[idioma]) {
        idiomasCombinados[idioma] = { libros: 0, manga: 0, leidos: 0, valor: 0 };
      }
      idiomasCombinados[idioma].manga++;
      idiomasCombinados[idioma].leidos += coleccion.tomosLeidos;
      if (coleccion.precioPorTomo) {
        idiomasCombinados[idioma].valor += coleccion.tomos.filter(t => 
          t.fechaCompra || 
          t.estado === 'leido' || 
          t.estado === 'leyendo'
        ).length * coleccion.precioPorTomo;
      }
    });
    
    // Estadísticas por formato
    const formatosCombinados: Record<string, { libros: number; manga: number; leidos: number; valor: number }> = {};
    
    libros.forEach(book => {
      const formato = book.formato || 'fisico';
      if (!formatosCombinados[formato]) {
        formatosCombinados[formato] = { libros: 0, manga: 0, leidos: 0, valor: 0 };
      }
      formatosCombinados[formato].libros++;
      if (book.estado === 'leido') formatosCombinados[formato].leidos++;
      if (book.precio) formatosCombinados[formato].valor += book.precio;
    });
    
    // Manga siempre es físico
    if (!formatosCombinados['fisico']) {
      formatosCombinados['fisico'] = { libros: 0, manga: 0, leidos: 0, valor: 0 };
    }
    formatosCombinados['fisico'].manga += coleccionesManga.length;
    formatosCombinados['fisico'].leidos += tomosMangaLeidos;
    formatosCombinados['fisico'].valor += valorManga;
    
    // Estadísticas de compra por mes (últimos 12 meses)
    const meses = Array.from({ length: 12 }, (_, i) => {
      const fecha = new Date();
      fecha.setMonth(fecha.getMonth() - i);
      return fecha.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' });
    }).reverse();
    
    const comprasPorMes = meses.map(mes => {
      const [mesStr, añoStr] = mes.split(' ');
      const año = parseInt('20' + añoStr);
      const mesNum = new Date(Date.parse(mesStr + ' 1, 2000')).getMonth();
      
      const librosComprados = libros.filter(book => {
        if (!book.fechaCompra) return false;
        const fechaCompra = new Date(book.fechaCompra);
        return fechaCompra.getFullYear() === año && fechaCompra.getMonth() === mesNum;
      }).length;
      
      const tomosComprados = coleccionesManga.reduce((sum, c) => {
        return sum + c.tomos.filter(t => {
          if (!t.fechaCompra) return false;
          const fechaCompra = new Date(t.fechaCompra);
          return fechaCompra.getFullYear() === año && fechaCompra.getMonth() === mesNum;
        }).length;
      }, 0);
      
      return { 
        mes, 
        libros: librosComprados, 
        manga: tomosComprados,
        total: librosComprados + tomosComprados
      };
    });
    
    // Estadísticas de lectura por mes
    const lecturasPorMes = meses.map(mes => {
      const [mesStr, añoStr] = mes.split(' ');
      const año = parseInt('20' + añoStr);
      const mesNum = new Date(Date.parse(mesStr + ' 1, 2000')).getMonth();
      
      const librosLeidos = libros.filter(book => {
        if (!book.fechaFin) return false;
        const fechaLectura = new Date(book.fechaFin);
        return fechaLectura.getFullYear() === año && fechaLectura.getMonth() === mesNum;
      }).length;
      
      const tomosLeidos = coleccionesManga.reduce((sum, c) => {
        return sum + c.tomos.filter(t => {
          if (!t.fechaLecturaReciente) return false;
          const fechaLectura = new Date(t.fechaLecturaReciente);
          return fechaLectura.getFullYear() === año && fechaLectura.getMonth() === mesNum;
        }).length;
      }, 0);
      
      return { 
        mes, 
        libros: librosLeidos, 
        manga: tomosLeidos,
        total: librosLeidos + tomosLeidos
      };
    });
    
    // Top autores/creadores combinados
    const autoresCombinados: Record<string, { libros: number; manga: number; leidos: number; valor: number }> = {};
    
    libros.forEach(book => {
      if (book.autor && book.estado === 'leido') {
        if (!autoresCombinados[book.autor]) {
          autoresCombinados[book.autor] = { libros: 0, manga: 0, leidos: 0, valor: 0 };
        }
        autoresCombinados[book.autor].libros++;
        autoresCombinados[book.autor].leidos++;
        if (book.precio) autoresCombinados[book.autor].valor += book.precio;
      }
    });
    
    coleccionesManga.forEach(coleccion => {
      if (coleccion.autor) {
        if (!autoresCombinados[coleccion.autor]) {
          autoresCombinados[coleccion.autor] = { libros: 0, manga: 0, leidos: 0, valor: 0 };
        }
        autoresCombinados[coleccion.autor].manga++;
        autoresCombinados[coleccion.autor].leidos += coleccion.tomosLeidos;
        if (coleccion.precioPorTomo) {
          autoresCombinados[coleccion.autor].valor += coleccion.tomos.filter(t => 
            t.fechaCompra || 
            t.estado === 'comprado' || 
            t.estado === 'leido' || 
            t.estado === 'leyendo'
          ).length * coleccion.precioPorTomo;
        }
      }
    });
    
    const topAutores = Object.entries(autoresCombinados)
      .map(([autor, stats]) => ({
        autor,
        total: stats.libros + stats.manga,
        leidos: stats.leidos,
        valor: stats.valor
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
    
    // Estadísticas de calificación combinadas
    const calificacionesCombinadas: Record<number, { libros: number; manga: number; total: number }> = {};
    
    libros.forEach(book => {
      if (book.calificacion && book.calificacion > 0) {
        const cal = Math.round(book.calificacion);
        if (!calificacionesCombinadas[cal]) {
          calificacionesCombinadas[cal] = { libros: 0, manga: 0, total: 0 };
        }
        calificacionesCombinadas[cal].libros++;
        calificacionesCombinadas[cal].total++;
      }
    });
    
    coleccionesManga.forEach(coleccion => {
      coleccion.tomos.forEach(tomo => {
        if (tomo.calificacion && tomo.calificacion > 0) {
          const cal = Math.round(tomo.calificacion);
          if (!calificacionesCombinadas[cal]) {
            calificacionesCombinadas[cal] = { libros: 0, manga: 0, total: 0 };
          }
          calificacionesCombinadas[cal].manga++;
          calificacionesCombinadas[cal].total++;
        }
      });
    });
    
    // Estadísticas de precio vs calificación
    const precioVsCalificacion = libros
      .filter(book => book.precio && book.precio > 0 && book.calificacion && book.calificacion > 0)
      .map(book => ({
        precio: book.precio,
        calificacion: book.calificacion,
        titulo: book.titulo,
        tipo: 'Libro'
      }));
    
    // Agregar manga
    coleccionesManga.forEach(coleccion => {
      if (coleccion.precioPorTomo && coleccion.precioPorTomo > 0) {
        coleccion.tomos.forEach(tomo => {
          if (tomo.calificacion && tomo.calificacion > 0) {
            precioVsCalificacion.push({
              precio: coleccion.precioPorTomo!,
              calificacion: tomo.calificacion,
              titulo: `${coleccion.titulo} #${tomo.numero}`,
              tipo: 'Manga'
            });
          }
        });
      }
    });
    
    return {
      // Totales
      totalLibros,
      totalManga,
      totalColeccion,
      totalLeidos,
      totalLeyendo,
      totalTBR,
      valorTotal,
      paginasTotales,
      
      // Por categorías
      generosCombinados,
      editorialesCombinadas,
      idiomasCombinados,
      formatosCombinados,
      
      // Tendencias temporales
      comprasPorMes,
      lecturasPorMes,
      
      // Rankings
      topAutores,
      calificacionesCombinadas,
      precioVsCalificacion
    };
  }, [state.libros, state.coleccionesManga]);

  // Colores para gráficos
  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#F97316', '#EC4899', '#84CC16', '#6366F1'];

  return (
    <div className="space-y-8">


      {/* Tarjetas de resumen global */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {[
          { title: 'Total Colección', value: statistics.totalColeccion, icon: BookOpen, color: 'bg-blue-500' },
          { title: 'Libros', value: statistics.totalLibros, icon: BookOpen, color: 'bg-green-500' },
          { title: 'Manga', value: statistics.totalManga, icon: BookMarked, color: 'bg-purple-500' },
          { title: 'Total Leídos', value: statistics.totalLeidos, icon: CheckCircle, color: 'bg-teal-500' },
          { title: 'Leyendo', value: statistics.totalLeyendo, icon: Clock, color: 'bg-yellow-500' },
          { title: 'Valor Total', value: `${statistics.valorTotal.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}`, icon: DollarSign, color: 'bg-emerald-500' }
        ].map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="glass-effect rounded-xl p-4 text-center"
          >
            <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg ${stat.color} mb-3`}>
              <stat.icon className="h-6 w-6 text-white" />
            </div>
            <div className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
              {stat.value}
            </div>
            <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
              {stat.title}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Gráfico de distribución por tipo */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="glass-effect rounded-xl p-6"
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
          <PieChartIcon className="h-5 w-5 mr-2 text-blue-500" />
          Distribución por Tipo de Contenido
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={[
                  { name: 'Libros', value: statistics.totalLibros, color: '#10B981' },
                  { name: 'Manga', value: statistics.totalManga, color: '#8B5CF6' }
                ]}
                cx="50%"
                cy="50%"
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                <Cell fill="#10B981" />
                <Cell fill="#8B5CF6" />
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          
          <div className="space-y-4">
            <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {statistics.totalLibros}
              </div>
              <div className="text-sm text-green-600 dark:text-green-400">
                Libros ({((statistics.totalLibros / statistics.totalColeccion) * 100).toFixed(1)}%)
              </div>
            </div>
            <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {statistics.totalManga}
              </div>
              <div className="text-sm text-purple-600 dark:text-purple-400">
                Manga ({((statistics.totalManga / statistics.totalColeccion) * 100).toFixed(1)}%)
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Gráfico de estado de lectura global */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="glass-effect rounded-xl p-6"
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
          <BarChart3 className="h-5 w-5 mr-2 text-green-500" />
          Estado de Lectura Global
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={[
            { estado: 'Leídos', libros: statistics.totalLeidos - (statistics.totalLeidos - statistics.totalLibros), manga: statistics.totalLeidos - (statistics.totalLeidos - statistics.totalManga), total: statistics.totalLeidos },
            { estado: 'Leyendo', libros: statistics.totalLeyendo - (statistics.totalLeyendo - statistics.totalLibros), manga: statistics.totalLeyendo - (statistics.totalLeyendo - statistics.totalManga), total: statistics.totalLeyendo },
            { estado: 'TBR', libros: statistics.totalTBR - (statistics.totalTBR - statistics.totalLibros), manga: statistics.totalTBR - (statistics.totalTBR - statistics.totalManga), total: statistics.totalTBR }
          ]}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="estado" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="libros" fill="#10B981" name="Libros" />
            <Bar dataKey="manga" fill="#8B5CF6" name="Manga" />
          </BarChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Gráficos de distribución por categorías */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Distribución por género */}
        {Object.keys(statistics.generosCombinados).length > 0 && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="glass-effect rounded-xl p-6"
          >
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
              <TargetIcon className="h-5 w-5 mr-2 text-green-500" />
              Distribución por Género
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={Object.entries(statistics.generosCombinados)
                    .map(([genero, stats], index) => ({
                      name: genero,
                      value: stats.libros + stats.manga,
                      color: colors[index % colors.length]
                    }))
                    .sort((a, b) => b.value - a.value)
                    .slice(0, 8)}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {Object.entries(statistics.generosCombinados)
                    .map(([genero, stats], index) => ({
                      name: genero,
                      value: stats.libros + stats.manga,
                      color: colors[index % colors.length]
                    }))
                    .sort((a, b) => b.value - a.value)
                    .slice(0, 8)
                    .map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                    ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </motion.div>
        )}

        {/* Distribución por editorial */}
        {Object.keys(statistics.editorialesCombinadas).length > 0 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="glass-effect rounded-xl p-6"
          >
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
              <BookOpen className="h-5 w-5 mr-2 text-purple-500" />
              Distribución por Editorial
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={Object.entries(statistics.editorialesCombinadas)
                    .map(([editorial, stats], index) => ({
                      name: editorial,
                      value: stats.libros + stats.manga,
                      color: colors[index % colors.length]
                    }))
                    .sort((a, b) => b.value - a.value)
                    .slice(0, 8)}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {Object.entries(statistics.editorialesCombinadas)
                    .map(([editorial, stats], index) => ({
                      name: editorial,
                      value: stats.libros + stats.manga,
                      color: colors[index % colors.length]
                    }))
                    .sort((a, b) => b.value - a.value)
                    .slice(0, 8)
                    .map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                    ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </motion.div>
        )}
      </div>


      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">



      </div>

      {/* Top autores/creadores */}
      {statistics.topAutores.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="glass-effect rounded-xl p-6"
        >
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
            <Users className="h-5 w-5 mr-2 text-blue-500" />
            Top Autores/Creadores
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {statistics.topAutores.map((autor, index) => (
              <div key={autor.autor} className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                    {index + 1}
                  </div>
                  <span className="font-medium text-slate-900 dark:text-white truncate">
                    {autor.autor}
                  </span>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">Total:</span>
                    <span className="font-medium">{autor.total}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">Leídos:</span>
                    <span className="font-medium">{autor.leidos}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">Valor:</span>
                    <span className="font-medium text-emerald-600 dark:text-emerald-400">
                      {autor.valor.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Distribución de calificaciones */}
      {Object.keys(statistics.calificacionesCombinadas).length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="glass-effect rounded-xl p-6"
        >
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
            <Star className="h-5 w-5 mr-2 text-yellow-500" />
            Distribución de Calificaciones
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={Object.entries(statistics.calificacionesCombinadas)
              .map(([cal, stats]) => ({
                calificacion: `${cal}⭐`,
                libros: stats.libros,
                manga: stats.manga,
                total: stats.total
              }))
              .sort((a, b) => parseInt(a.calificacion) - parseInt(b.calificacion))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="calificacion" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="libros" fill="#10B981" name="Libros" />
              <Bar dataKey="manga" fill="#8B5CF6" name="Manga" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      )}

      {/* Gráfico de precio vs calificación */}
      {statistics.precioVsCalificacion.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1 }}
          className="glass-effect rounded-xl p-6"
        >
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2 text-orange-500" />
            Precio vs Calificación
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart data={statistics.precioVsCalificacion}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" dataKey="precio" name="Precio" unit="€" />
              <YAxis type="number" dataKey="calificacion" name="Calificación" domain={[0, 5]} />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} />
              <Legend />
              <Scatter name="Libros" data={statistics.precioVsCalificacion.filter(item => item.tipo === 'Libro')} fill="#10B981" />
              <Scatter name="Manga" data={statistics.precioVsCalificacion.filter(item => item.tipo === 'Manga')} fill="#8B5CF6" />
            </ScatterChart>
          </ResponsiveContainer>
        </motion.div>
      )}

      {/* Estadísticas de formato */}
      {Object.keys(statistics.formatosCombinados).length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="glass-effect rounded-xl p-6"
        >
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
            <BookOpen className="h-5 w-5 mr-2 text-indigo-500" />
            Distribución por Formato
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={Object.entries(statistics.formatosCombinados).map(([formato, stats]) => ({
              formato: formato.charAt(0).toUpperCase() + formato.slice(1),
              libros: stats.libros,
              manga: stats.manga,
              total: stats.libros + stats.manga
            }))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="formato" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="libros" fill="#10B981" name="Libros" />
              <Bar dataKey="manga" fill="#8B5CF6" name="Manga" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      )}
    </div>
  );
};

export default GlobalStatistics;